import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Paper, Stack } from '@mui/material';
import { EngineeringRounded, PersonRounded } from '@mui/icons-material';
import AttachFile from '@mui/icons-material/AttachFile';
import { IBuildMessages, MessageSource } from '../../../../models/phases-of-contruction';
import { downloadFile } from '../../../../helpers';
import axios from 'axios';

interface IChatWindow {
  messages: IBuildMessages[];
}

export const ChatWindow: React.FC<IChatWindow> = ({ messages }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const formatTimestamp = useCallback((timestampString: string) => {
    const timestamp = new Date(timestampString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(timestamp);
  }, []);

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Only scroll when new messages are added, not on every messages array change
  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  const handleFileClick = useCallback(async (fileUrl: string, fileName?: string) => {
    try {
      const response = await axios.get(fileUrl, {
        responseType: 'blob'
      });

      const finalFileName = fileName || fileUrl.split('/').pop() || 'download';
      downloadFile(response.data, finalFileName);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to opening in new window if download fails
      window.open(fileUrl, '_blank');
    }
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        height: { xs: '400px', md: '500px' },
        minWidth: '175px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'backgroundBlue.main',
        borderRadius: 0,
        boxShadow: 'none',
        border: 'none'
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.400',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'grey.500'
            }
          }
        }}
      >
        <Stack spacing={2}>
          {messages
            .slice()
            .reverse()
            .map(msg => {
              const isPurchaser = msg.messageSource === MessageSource.Purchaser;

              return (
                <Box
                  key={msg.buildCommunicationMessagesId}
                  sx={{
                    display: 'flex',
                    justifyContent: isPurchaser ? 'flex-end' : 'flex-start',
                    mb: 1
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '85%',
                      p: 2,
                      pr: msg.fileUrl ? 4 : 2,
                      borderRadius: 2,
                      backgroundColor: 'white',
                      color: 'text.primary',
                      position: 'relative',
                      boxShadow: 1
                    }}
                  >
                    {/* File indicator */}
                    {msg.fileUrl && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() =>
                          handleFileClick(
                            msg.fileUrl!,
                            `attachment_${msg.buildCommunicationMessagesId}`
                          )
                        }
                      >
                        <AttachFile sx={{ fontSize: 16, color: 'grey.600' }} />
                      </Box>
                    )}

                    {/* Name with icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: isPurchaser ? 'tertiary.main' : 'messageGreen.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 0.5
                        }}
                      >
                        {isPurchaser ? (
                          <PersonRounded sx={{ fontSize: 16, color: 'white' }} />
                        ) : (
                          <EngineeringRounded sx={{ fontSize: 16, color: 'white' }} />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {msg.createdByFullName}
                      </Typography>
                    </Box>

                    {/* Timestamp */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1,
                        opacity: 0.7,
                        fontSize: '0.75rem'
                      }}
                    >
                      {formatTimestamp(msg.createdAt)}
                    </Typography>

                    {/* Message text */}
                    <Typography variant="body1" sx={{ lineHeight: 1.4, mb: msg.fileUrl ? 1 : 0 }}>
                      {msg.message}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
        </Stack>
      </Box>
    </Paper>
  );
};
